import type {
    ColSpanParams,
    ColumnState,
    CustomHeaderGroupProps,
    EditableCallbackParams,
    ICellRendererParams,
    IHeaderParams,
    ITooltipParams,
} from "@/ui/molecules/Grid";
import GridHeaderComponent from "@/ui/molecules/GridHeaderComponent";
import GridDuplicateHeaderComponent from "@/ui/molecules/GridDuplicateHeaderComponent";
import GridHeaderGroupComponent from "@/ui/molecules/GridHeaderGroupComponent";

import GridRendererNumber from "@/ui/organisms/GridRendererNumber";

import { formatStringDateTime } from "@/utils/dates";

import type {
    TActiveColumnChange,
    TRowReviewWithOperatingStatements,
} from "../typesGrids";
import type { GridReviewProps } from "../GridReview";
import type { ExtractionsErrorListProps } from "../ExtractionsSidebar/ExtractionsErrorList";
import { formatNumber } from "../utils";
import {
    createCalculateColSpanForMergingDuplicatedColumnsCellsWithSameValue,
    createShouldMergeDuplicatedColumnsCellsWithSameValue,
} from "../calculateColSpanForMergingDuplicatedColumnsCellsWithSameValue";

import GridReviewRendererFirstColumn from "./GridReviewRendererFirstColumn";

const SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER = 8;
const ADDITIONAL_SPACE_IN_PIXELS = 32;
const calculateColumnWidths = (value: number) => {
    const columnWidth =
        value * SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER +
        ADDITIONAL_SPACE_IN_PIXELS;
    const columnMinWidth = value * SPACE_IN_PIXELS_PER_NUMERIC_CHARACTER;
    return {
        columnWidth,
        columnMinWidth,
    };
};

export const makeGridReviewColumnDefs = ({
    isExtractionCompleted,
    allowEditing,
    columnDefsOperatingStatementsFromProps,
    longestOperatingStatementAndTotalsNumericValue,
    errorGroups,
    activeColumns,
    extractionsInfo,
    setActiveColumn,
    initialState,
}: {
    isExtractionCompleted: boolean;
    allowEditing: boolean;
    columnDefsOperatingStatementsFromProps: GridReviewProps["columnDefsOperatingStatements"];
    longestOperatingStatementAndTotalsNumericValue: number;
    errorGroups: ExtractionsErrorListProps["errors"];
    activeColumns: Record<string, TActiveColumnChange> | null;
    extractionsInfo:
        | {
              fileName: string;
              extractionId: number;
              sheetName: string;
              uploadedAt?: string;
          }[]
        | null;
    setActiveColumn: (label: string, extractionId: number) => void;
    initialState?: ColumnState[];
    extractionId: number;
}) => {
    const getColumnWidth = (
        field: string,
        headerName: string,
        defaultWidth: number,
    ) => {
        if (initialState?.find(s => s.colId === field)) {
            return initialState.find(s => s.colId === field)?.width;
        }
        const headerNameWidth = headerName.length * 8;
        return Math.max(defaultWidth, headerNameWidth);
    };

    const cellStylesWithEllipsis = {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        display: "block",
    };
    const commonColumnParameters = {
        suppressMovable: true,
        cellStyle: cellStylesWithEllipsis,
    };

    const columnDefAccount = {
        headerName: "Account Mapping",
        headerComponent: GridHeaderComponent,
        field: "account",
        width: getColumnWidth("account", "Account Mapping", 348),
        minWidth: 348,
        resizable: false,
        cellRenderer: GridReviewRendererFirstColumn,
        cellStyle: {
            ...commonColumnParameters.cellStyle,
            paddingRight: "0px",
            paddingLeft: "0px",
        },
        colSpan: (params: ColSpanParams<TRowReviewWithOperatingStatements>) => {
            return params.data?.type === "HEADER"
                ? Number.MAX_SAFE_INTEGER // @HACK: This is a hack to make the category row span all columns
                : 0;
        },
    };

    const columnOperatingStatementWidths = calculateColumnWidths(
        longestOperatingStatementAndTotalsNumericValue,
    );

    const handleChangeActiveColumn = (label: string, extractionId: number) => {
        setActiveColumn(label, extractionId);
    };

    const columnDefsOperatingStatements =
        columnDefsOperatingStatementsFromProps.map(
            operatingStatementsColumnDef => {
                const baseColumnDef = {
                    editable: (
                        params: EditableCallbackParams<TRowReviewWithOperatingStatements>,
                    ) => allowEditing && params.data?.type !== "ACCOUNT",
                    width: getColumnWidth(
                        operatingStatementsColumnDef.field,
                        operatingStatementsColumnDef.headerName,
                        columnOperatingStatementWidths.columnWidth,
                    ),
                    minWidth: columnOperatingStatementWidths.columnMinWidth,
                    tooltipValueGetter: (
                        params: ITooltipParams<TRowReviewWithOperatingStatements>,
                    ) => {
                        if (!params.data) return null;
                        const value =
                            params.data[operatingStatementsColumnDef.field];
                        if (typeof value !== "number") return null;
                        return formatNumber(value);
                    },
                    cellStyle: {
                        ...commonColumnParameters.cellStyle,
                        paddingRight: "0px",
                        paddingLeft: "0px",
                    },
                };
                if (
                    operatingStatementsColumnDef.children &&
                    operatingStatementsColumnDef.children.length > 0 &&
                    !isExtractionCompleted &&
                    operatingStatementsColumnDef.duplicatedExtractionId ===
                        undefined
                ) {
                    const activeColumn =
                        activeColumns?.[
                            operatingStatementsColumnDef.headerName
                        ];
                    const selectedField =
                        [
                            ...operatingStatementsColumnDef.children,
                            operatingStatementsColumnDef,
                        ].find(c => c.extractionId === activeColumn?.to)
                            ?.field ?? "";
                    const headerInfo = extractionsInfo?.find(
                        f =>
                            f.extractionId ===
                            Number(operatingStatementsColumnDef.extractionId),
                    );

                    const headerName = `${headerInfo?.sheetName} Table ${headerInfo?.extractionId} > ${
                        headerInfo?.fileName
                    }`;
                    const tooltipContent = `Data from file: "${headerInfo?.fileName}", uploaded at: ${headerInfo?.uploadedAt !== undefined && formatStringDateTime(headerInfo?.uploadedAt)}, sheet: "${headerInfo?.sheetName}", extraction ID: ${
                        headerInfo?.extractionId
                    }.`;

                    return {
                        headerName: operatingStatementsColumnDef.headerName,
                        headerGroupComponent: (
                            props: CustomHeaderGroupProps,
                        ) => (
                            <GridHeaderGroupComponent
                                {...props}
                                textAlign="center"
                            />
                        ),
                        groupId: operatingStatementsColumnDef.headerName,
                        marryChildren: true,
                        children: [
                            {
                                ...baseColumnDef,
                                columnGroupShow:
                                    activeColumn?.to ===
                                    operatingStatementsColumnDef.extractionId
                                        ? undefined
                                        : "open",
                                field: operatingStatementsColumnDef.field,
                                headerName: headerName,
                                colSpan:
                                    createCalculateColSpanForMergingDuplicatedColumnsCellsWithSameValue(
                                        {
                                            relyOnGridApiExpandedValue: true,
                                        },
                                    )<TRowReviewWithOperatingStatements>,
                                headerComponent: (params: IHeaderParams) => (
                                    <GridDuplicateHeaderComponent
                                        {...params}
                                        groupHeaderName={
                                            operatingStatementsColumnDef.headerName
                                        }
                                        selectedValue={selectedField}
                                        tooltipContent={tooltipContent}
                                        onChangeActiveColumn={() =>
                                            handleChangeActiveColumn(
                                                operatingStatementsColumnDef.headerName,
                                                Number(
                                                    operatingStatementsColumnDef.extractionId,
                                                ),
                                            )
                                        }
                                        isCurrent={true}
                                        isAltered={
                                            activeColumn?.from !==
                                                activeColumn?.to &&
                                            activeColumn?.to ===
                                                operatingStatementsColumnDef.extractionId
                                        }
                                    />
                                ),
                                suppressHeaderContextMenu: true,
                                cellRenderer: (params: ICellRendererParams) => {
                                    const isCellMergedInGroupAsItHasSameValues =
                                        createShouldMergeDuplicatedColumnsCellsWithSameValue(
                                            {
                                                relyOnGridApiExpandedValue:
                                                    true,
                                            },
                                        )(params);
                                    return (
                                        <GridRendererNumber
                                            errorGroups={errorGroups}
                                            {...params}
                                            isGreyedOut={
                                                !isCellMergedInGroupAsItHasSameValues &&
                                                operatingStatementsColumnDef.extractionId !==
                                                    activeColumn?.to
                                            }
                                        />
                                    );
                                },
                            },
                            ...operatingStatementsColumnDef.children.map(
                                child => {
                                    const childField = child.field;
                                    const childHeaderInfo =
                                        extractionsInfo?.find(
                                            f =>
                                                f.extractionId ===
                                                Number(child.extractionId),
                                        );

                                    const childHeaderName = `${childHeaderInfo?.sheetName} Table ${
                                        child.extractionId
                                    } > ${childHeaderInfo?.fileName}`;
                                    const childTooltipContent = `Data from file: "${childHeaderInfo?.fileName}", uploaded at: ${childHeaderInfo?.uploadedAt !== undefined && formatStringDateTime(childHeaderInfo?.uploadedAt)}, sheet: "${childHeaderInfo?.sheetName}", extraction ID: ${
                                        childHeaderInfo?.extractionId
                                    }.`;

                                    return {
                                        ...baseColumnDef,
                                        columnGroupShow:
                                            activeColumn?.to ===
                                            child.extractionId
                                                ? undefined
                                                : "open",
                                        field: childField,
                                        headerName: childHeaderName,
                                        headerComponent: (
                                            params: IHeaderParams,
                                        ) => (
                                            <GridDuplicateHeaderComponent
                                                {...params}
                                                groupHeaderName={
                                                    operatingStatementsColumnDef.headerName
                                                }
                                                selectedValue={selectedField}
                                                tooltipContent={
                                                    childTooltipContent
                                                }
                                                onChangeActiveColumn={() =>
                                                    handleChangeActiveColumn(
                                                        operatingStatementsColumnDef.headerName,
                                                        child.extractionId,
                                                    )
                                                }
                                                isAltered={
                                                    activeColumn?.from !==
                                                        activeColumn?.to &&
                                                    activeColumn?.to ===
                                                        child.extractionId
                                                }
                                            />
                                        ),
                                        suppressHeaderContextMenu: true,
                                        cellRenderer: (
                                            params: ICellRendererParams,
                                        ) => {
                                            const isCellMergedInGroupAsItHasSameValues =
                                                createShouldMergeDuplicatedColumnsCellsWithSameValue(
                                                    {
                                                        relyOnGridApiExpandedValue:
                                                            true,
                                                    },
                                                )(params);
                                            return (
                                                <GridRendererNumber
                                                    errorGroups={errorGroups}
                                                    {...params}
                                                    isGreyedOut={
                                                        !isCellMergedInGroupAsItHasSameValues &&
                                                        child.extractionId !==
                                                            activeColumn?.to
                                                    }
                                                />
                                            );
                                        },
                                    };
                                },
                            ),
                        ],
                    };
                } else {
                    // @TODO: Currently we only show 1 of the duplicates even if there are multiple, we should improve this
                    const tooltipContent =
                        operatingStatementsColumnDef.duplicatedExtractionId !==
                        undefined
                            ? `This column has duplicate in ${
                                  extractionsInfo?.find(
                                      f =>
                                          f.extractionId ===
                                          operatingStatementsColumnDef.duplicatedExtractionId,
                                  )?.fileName
                              } (Extraction ID: ${
                                  operatingStatementsColumnDef.duplicatedExtractionId
                              })`
                            : undefined;

                    return {
                        headerComponent: (params: IHeaderParams) => (
                            <GridHeaderComponent
                                {...params}
                                tooltipContent={tooltipContent}
                                textAlign="center"
                                extraInfo={
                                    operatingStatementsColumnDef.duplicatedExtractionId !==
                                    undefined
                                        ? true
                                        : undefined
                                }
                            />
                        ),
                        ...baseColumnDef,
                        resizable: false,
                        field: operatingStatementsColumnDef.field,
                        headerName: operatingStatementsColumnDef.headerName,
                        cellRenderer: (params: ICellRendererParams) => (
                            <GridRendererNumber
                                errorGroups={errorGroups}
                                {...params}
                            />
                        ),
                    };
                }
            },
        );

    const columnDefs = [columnDefAccount, ...columnDefsOperatingStatements];
    return columnDefs;
};
